import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  Alert,
  Snackbar,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import api from "../../services/api";

const OrderManagement = () => {
  const { storeId } = useParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [loading, setLoading] = useState({});

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchOrders = async () => {
    if (!storeId) return;
    try {
      setError(null);
      const res = await api.get(`/api/orders`, {
        params: { storeId },
      });
      const ordersData = Array.isArray(res.data) ? res.data : [];
      console.log("取得した注文データ:", ordersData.length, "件");
      setOrders(ordersData);
    } catch (err) {
      console.error("注文データの取得に失敗しました:", err);
      setError("注文データの取得に失敗しました");
      setOrders([]);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "CONFIRMED":
        return "info";
      case "PREPARING":
        return "info";
      case "READY":
        return "primary";
      case "COMPLETED":
        return "success";
      case "DELIVERED":
        return "success";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "未対応";
      case "CONFIRMED":
        return "確認済み";
      case "PREPARING":
        return "準備中";
      case "READY":
        return "準備完了";
      case "COMPLETED":
        return "完了";
      case "DELIVERED":
        return "配達済み";
      case "CANCELLED":
        return "キャンセル";
      default:
        return status;
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "success";
      case "UNPAID":
        return "warning";
      case "REFUNDED":
        return "error";
      case "PARTIALLY_REFUNDED":
        return "warning";
      default:
        return "default";
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "PAID":
        return "支払済";
      case "UNPAID":
        return "未払い";
      case "REFUNDED":
        return "返金済";
      case "PARTIALLY_REFUNDED":
        return "一部返金";
      default:
        return status || "-";
    }
  };

  const handleConfirmOrder = async (orderId) => {
    try {
      setLoading({ ...loading, [orderId]: true });
      await api.patch(`/api/orders/${orderId}/status`, {
        status: "CONFIRMED",
        note: "注文を確認しました",
      });
      setSnackbar({ open: true, message: "注文を確認しました" });
      await fetchOrders();
    } catch (err) {
      console.error("注文確認エラー:", err);
      setSnackbar({
        open: true,
        message: "注文の確認に失敗しました",
      });
    } finally {
      setLoading({ ...loading, [orderId]: false });
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      setLoading({ ...loading, [orderId]: true });
      await api.patch(`/api/orders/${orderId}/status`, {
        status: "CANCELLED",
        note: "注文をキャンセルしました",
      });
      setSnackbar({ open: true, message: "注文をキャンセルしました" });
      await fetchOrders();
    } catch (err) {
      console.error("注文キャンセルエラー:", err);
      setSnackbar({
        open: true,
        message: "注文のキャンセルに失敗しました",
      });
    } finally {
      setLoading({ ...loading, [orderId]: false });
    }
  };

  const handleOrderClick = (orderId) => {
    // 注文詳細ページに遷移（必要に応じて実装）
    console.log("注文をクリック:", orderId);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        注文管理
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>注文番号</TableCell>
                <TableCell>注文内容</TableCell>
                <TableCell>合計金額</TableCell>
                <TableCell>注文時間</TableCell>
                <TableCell>注文状況</TableCell>
                <TableCell>支払い</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    注文がありません
                  </TableCell>
                </TableRow>
              ) : (
                orders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                    <TableRow key={order._id || order.id} data-testid="order-row">
                    <TableCell>
                      <Button
                        onClick={() => handleOrderClick(order._id || order.id)}
                        sx={{ textTransform: "none", p: 0, minWidth: "auto", fontSize: "0.75rem" }}
                        data-testid={`order-id-${order._id || order.id}`}
                      >
                        {(order._id || order.id).slice(0, 8)}...
                      </Button>
                    </TableCell>
                    <TableCell>
                      {order.items?.map((item, index) => (
                        <div key={index}>
                          {item.name || item.productName || item.product?.name || ""} × {item.quantity || 0}
                        </div>
                      )) || ""}
                    </TableCell>
                    <TableCell>
                      ¥{(order.totalAmount || order.total_amount || order.total || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleString()
                        : order.created_at
                        ? new Date(order.created_at).toLocaleString()
                        : ""}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(order.status)}
                        color={getStatusColor(order.status)}
                        size="small"
                        data-testid="order-status-badge"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPaymentStatusLabel(order.payment_status || order.paymentStatus)}
                        color={getPaymentStatusColor(order.payment_status || order.paymentStatus)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="success"
                        size="small"
                        onClick={() =>
                          handleConfirmOrder(order._id || order.id)
                        }
                        disabled={loading[order._id || order.id] || order.status === "COMPLETED" || order.status === "CANCELLED"}
                        aria-label="確認"
                        data-testid={`confirm-order-${order._id || order.id}`}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleCancelOrder(order._id || order.id)}
                        disabled={loading[order._id || order.id] || order.status === "COMPLETED" || order.status === "CANCELLED"}
                        aria-label="キャンセル"
                      >
                        <CancelIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={orders.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数"
        />
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default OrderManagement;
